import { useEffect, useState } from 'react';
import { Box, Button, Flex, Stack, Text, Badge } from '@chakra-ui/react';
import { PencilSimple, Plus } from '@phosphor-icons/react';
import { v4 as uuidV4 } from 'uuid';
import { fetchAPI } from '@/api/fetch';
import { apiUpdateJobApplicationQuestions } from '@/api/services';
import { ApplicationQuestion } from '@/types/ApplicationQuestion';
import { Job } from '@/types/Job';
import { ApplicationQuestionType, getApplicationQuestionTypeLabel } from '@/types/enums';
import QuestionEditor from './QuestionEditor';

interface ApplicationQuestionsProps {
  job: Job;
  onUpdate: (updatedJob: Job) => void;
}

export default function ApplicationQuestions({ job, onUpdate }: ApplicationQuestionsProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [questions, setQuestions] = useState<ApplicationQuestion[]>(
    job.application_questions || []
  );
  const [questionErrors, setQuestionErrors] = useState<{ [key: string]: string }>({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync questions with job.application_questions when it changes (e.g., from dialog)
  useEffect(() => {
    if (!isEditing) {
      setQuestions(job.application_questions || []);
    }
  }, [job.application_questions, isEditing]);

  const handleAddQuestion = () => {
    setQuestions([
      ...questions,
      {
        question_id: uuidV4(),
        question: '',
        question_type: 'text',
        required: false
      }
    ]);
  };

  const handleRemoveQuestion = (questionId: string) => {
    setQuestions(questions.filter((q) => q.question_id !== questionId));
    // Clear error for removed question
    const newErrors = { ...questionErrors };
    delete newErrors[questionId];
    setQuestionErrors(newErrors);
  };

  const handleQuestionChange = (questionId: string, value: string) => {
    setQuestions(
      questions.map((q) => (q.question_id === questionId ? { ...q, question: value } : q))
    );
    // Clear error when user starts typing
    if (questionErrors[questionId]) {
      const newErrors = { ...questionErrors };
      delete newErrors[questionId];
      setQuestionErrors(newErrors);
    }
  };

  const handleQuestionTypeChange = (questionId: string, type: ApplicationQuestionType) => {
    setQuestions(
      questions.map((q) => {
        if (q.question_id === questionId) {
          const updated: ApplicationQuestion = { ...q, question_type: type };
          // Remove options if switching away from multiple_choice
          if (type !== 'multiple_choice') {
            delete updated.options;
          } else if (!updated.options) {
            // Add default options for multiple_choice
            updated.options = [''];
          }
          return updated;
        }
        return q;
      })
    );
  };

  const handleOptionChange = (questionId: string, optionIndex: number, value: string) => {
    setQuestions(
      questions.map((q) => {
        if (q.question_id === questionId && q.options) {
          const newOptions = [...q.options];
          newOptions[optionIndex] = value;
          return { ...q, options: newOptions };
        }
        return q;
      })
    );
  };

  const handleAddOption = (questionId: string) => {
    setQuestions(
      questions.map((q) => {
        if (q.question_id === questionId) {
          return { ...q, options: [...(q.options || []), ''] };
        }
        return q;
      })
    );
  };

  const handleRemoveOption = (questionId: string, optionIndex: number) => {
    setQuestions(
      questions.map((q) => {
        if (q.question_id === questionId && q.options) {
          const newOptions = q.options.filter((_, i) => i !== optionIndex);
          // Keep at least one option
          return { ...q, options: newOptions.length > 0 ? newOptions : [''] };
        }
        return q;
      })
    );
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError(null);

    // If there are no questions, add a default empty question
    if (questions.length === 0) {
      setQuestions([
        {
          question_id: uuidV4(),
          question: '',
          question_type: 'text',
          required: false
        }
      ]);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setQuestions(job.application_questions || []);
    setQuestionErrors({});
    setError(null);
  };

  const handleSave = async () => {
    // Validate questions
    const errors: { [key: string]: string } = {};
    const nonEmptyQuestions = questions.filter((q) => q.question.trim());

    // Check if there are any empty questions
    questions.forEach((q) => {
      if (!q.question.trim()) {
        errors[q.question_id] = 'Question cannot be empty';
      }
      // Validate multiple choice questions have at least one non-empty option
      if (q.question_type === 'multiple_choice' && q.question.trim()) {
        const nonEmptyOptions = (q.options || []).filter((opt) => opt.trim());
        if (nonEmptyOptions.length === 0) {
          errors[q.question_id] = 'Multiple choice questions must have at least one option';
        }
      }
    });

    if (Object.keys(errors).length > 0) {
      setQuestionErrors(errors);
      return;
    }

    // Clean up options for multiple choice questions (remove empty options)
    const cleanedQuestions = nonEmptyQuestions.map((q) => {
      if (q.question_type === 'multiple_choice' && q.options) {
        return {
          ...q,
          required: true,
          options: q.options.filter((opt) => opt.trim())
        };
      }

      return {
        ...q,
        required: true
      };
    });

    setIsSaving(true);
    setError(null);

    try {
      const response = await fetchAPI({
        request: () =>
          apiUpdateJobApplicationQuestions({
            jobId: job.job_id,
            payload: { application_questions: cleanedQuestions }
          })
      });

      if (response.success) {
        // Update parent component with new questions
        onUpdate({
          ...job,
          application_questions: cleanedQuestions
        });
        setQuestions(cleanedQuestions);
        setIsEditing(false);
      } else {
        setError(response.message || 'Failed to update application questions');
      }
    } catch (err) {
      console.error('Error updating application questions:', err);
      setError('An error occurred while updating application questions');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box w="100%" mt="32px">
      <Flex justifyContent="space-between" alignItems="center" mb="24px">
        <Box>
          <Text fontSize="24px" fontWeight="bold" color="neutrals.900">
            Application Questions
          </Text>
          <Text fontSize="16px" color="neutrals.600" mt="4px">
            Custom questions candidates will answer when applying for this job
          </Text>
        </Box>
        {!isEditing && (
          <Button
            variant="outline"
            size="md"
            onClick={handleEdit}
            borderRadius="8px"
            borderColor="primary.900"
            color="primary.900"
            fontSize="16px"
            _hover={{ bg: 'primary.50' }}
          >
            <PencilSimple size={18} weight="bold" />
            Edit Questions
          </Button>
        )}
      </Flex>

      {error && (
        <Box
          bg="error.50"
          border="1px solid"
          borderColor="error.300"
          borderRadius="8px"
          p="16px"
          mb="24px"
        >
          <Text color="error.700" fontSize="14px">
            {error}
          </Text>
        </Box>
      )}

      {isEditing ? (
        <Box
          bg="background.secondary"
          border="1px solid"
          borderColor="neutrals.200"
          borderRadius="12px"
          p="32px"
        >
          <Stack gap="24px">
            {questions.length > 0 ? (
              questions.map((question, index) => (
                <QuestionEditor
                  key={question.question_id}
                  question={question}
                  index={index}
                  isDisabled={isSaving}
                  error={questionErrors[question.question_id]}
                  onQuestionChange={handleQuestionChange}
                  onQuestionTypeChange={handleQuestionTypeChange}
                  onRemoveQuestion={handleRemoveQuestion}
                  onOptionChange={handleOptionChange}
                  onAddOption={handleAddOption}
                  onRemoveOption={handleRemoveOption}
                />
              ))
            ) : (
              <Text fontSize="16px" color="neutrals.500" textAlign="center" py="32px">
                No questions added yet. Click "Add Question" to create your first question.
              </Text>
            )}

            <Button
              variant="outline"
              size="md"
              onClick={handleAddQuestion}
              disabled={isSaving}
              borderRadius="8px"
              borderColor="primary.900"
              color="primary.900"
              fontSize="16px"
              _hover={{ bg: 'primary.50' }}
              w="fit-content"
            >
              <Plus size={18} weight="bold" />
              Add Question
            </Button>
          </Stack>

          <Flex gap="12px" mt="32px" justifyContent="flex-end">
            <Button
              variant="outline"
              size="md"
              onClick={handleCancel}
              disabled={isSaving}
              borderRadius="8px"
              borderColor="neutrals.300"
              color="neutrals.700"
              fontSize="16px"
              _hover={{ bg: 'neutrals.100' }}
            >
              Cancel
            </Button>
            <Button
              size="md"
              onClick={handleSave}
              loading={isSaving}
              disabled={isSaving}
              borderRadius="8px"
              bg="primary.900"
              color="neutrals.0"
              fontSize="16px"
              _hover={{ bg: 'primary.700' }}
            >
              Save Changes
            </Button>
          </Flex>
        </Box>
      ) : (
        <Box
          bg="background.secondary"
          border="1px solid"
          borderColor="neutrals.200"
          borderRadius="12px"
          p="32px"
        >
          {questions.length > 0 ? (
            <Stack gap="20px">
              {questions.map((question, index) => (
                <Box key={question.question_id}>
                  <Flex alignItems="center" gap="12px" mb="8px">
                    <Text fontSize="16px" fontWeight="semibold" color="neutrals.600">
                      Question {index + 1}
                    </Text>
                    <Badge
                      fontSize="12px"
                      color="neutrals.700"
                      px="8px"
                      py="2px"
                      bg="neutrals.100"
                      borderRadius="full"
                    >
                      {getApplicationQuestionTypeLabel(question.question_type)}
                    </Badge>
                  </Flex>
                  <Text
                    fontSize="16px"
                    color="neutrals.900"
                    mb={question.question_type === 'multiple_choice' ? '12px' : '0'}
                  >
                    {question.question}
                  </Text>
                  {question.question_type === 'multiple_choice' &&
                    question.options &&
                    question.options.length > 0 && (
                      <Box>
                        <Text fontSize="14px" color="neutrals.500" mb="6px">
                          Options:
                        </Text>
                        <Stack gap="4px" ml="16px" fontSize="14px">
                          {question.options.map((option, optIndex) => (
                            <Text key={optIndex} fontSize="14px" color="neutrals.700">
                              • {option}
                            </Text>
                          ))}
                        </Stack>
                      </Box>
                    )}
                </Box>
              ))}
            </Stack>
          ) : (
            <Flex
              direction="column"
              align="center"
              justify="center"
              py="64px"
              gap="16px"
              textAlign="center"
            >
              <Text fontSize="18px" fontWeight="semibold" color="neutrals.700">
                No Application Questions
              </Text>
              <Text fontSize="16px" color="neutrals.500" maxW="500px">
                Add custom questions to gather more information from candidates when they apply for
                this job.
              </Text>
              <Button
                variant="outline"
                size="md"
                onClick={handleEdit}
                mt="8px"
                borderRadius="8px"
                borderColor="primary.900"
                color="primary.900"
                fontSize="16px"
                _hover={{ bg: 'primary.50' }}
              >
                <Plus size={18} weight="bold" />
                Add Questions
              </Button>
            </Flex>
          )}
        </Box>
      )}
    </Box>
  );
}
